import { Link } from "react-router-dom";
import { Cookie, X, ShieldCheck, Globe } from "lucide-react";
import { useState, useEffect } from "react";
import { useCookieConsent } from "../contexts/CookieConsentContext";

/**
 * CMSR GDPR 1.5 — Banner cookie
 * - "Acceptă toate" | "Respinge opționale" | "Preferințe cookies"
 * - "Necesare" prebifat și blocat.
 * - Cookies opționale DEZACTIVATE implicit.
 * - Butonul de refuz este la fel de vizibil ca cel de acceptare.
 */
export function CookieBanner() {
  const { consent, hasChosen, preferencesOpen, acceptAll, rejectOptional, savePreferences, reopenPreferences, closePreferences } = useCookieConsent();
  const [draft, setDraft] = useState({ thirdParty: consent.thirdParty });

  useEffect(() => { setDraft({ thirdParty: consent.thirdParty }); }, [consent.thirdParty, preferencesOpen]);

  const showBanner = !hasChosen && !preferencesOpen;

  return (
    <>
      {showBanner && (
        <div
          role="dialog"
          aria-label="Consimțământ cookies"
          aria-describedby="cookie-banner-desc"
          className="fixed inset-x-0 bottom-0 z-[300] p-4 sm:p-5"
          data-testid="cookie-banner"
        >
          <div className="max-w-5xl mx-auto bg-white border-2 border-brand-yellow rounded-2xl shadow-2xl overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-center gap-5 p-5 sm:p-6">
              <div className="shrink-0 w-12 h-12 rounded-full bg-brand-yellow/20 text-brand-navy flex items-center justify-center">
                <Cookie className="w-6 h-6" />
              </div>
              <div className="flex-1 text-sm text-brand-navy/85 leading-relaxed" id="cookie-banner-desc">
                <p className="font-display font-bold text-brand-navy text-base mb-1">Acest website foloseste cookie-uri</p>
                <p>
                  Website-ul <strong>ro-dental.ro</strong>, detinut si operat de <strong>Cabinet Medical Individual Dr. Cornelia Maria Biriș</strong>, foloseste cookie-uri necesare pentru a va asigura accesul la toate functionalitatile website-ului, dar si pentru securitatea acestuia.
                  Pe langa acestea, website-ul incorporeaza continut de la <strong>terti</strong> (harta Google Maps) care poate seta cookie-uri, exclusiv in baza consimtamantului expres exprimat de dumneavoastra.
                  Pentru mai multe detalii va rugam sa consultati <Link to="/politica-cookies" className="underline font-semibold text-brand-red hover:text-brand-navy">Politica de utilizare a Modulelor Cookie</Link>.
                  Va puteti exprima consimtamantul cu ajutorul butoanelor alaturate sau realizand propria selectie prin „Preferințe cookies". Puteti modifica preferintele in orice moment, prin optiunea „Preferințe cookies" din subsolul site-ului.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row md:flex-col gap-2 shrink-0 min-w-[180px]">
                <button
                  type="button"
                  onClick={acceptAll}
                  className="px-5 py-2.5 rounded-full bg-brand-red text-white font-semibold text-sm shadow-md hover:bg-brand-redSoft transition-colors"
                  data-testid="cookie-accept-all"
                >
                  Acceptă toate
                </button>
                <button
                  type="button"
                  onClick={rejectOptional}
                  className="px-5 py-2.5 rounded-full bg-brand-navy text-white font-semibold text-sm shadow-md hover:bg-brand-navyDeep transition-colors"
                  data-testid="cookie-reject-optional"
                >
                  Respinge opționale
                </button>
                <button
                  type="button"
                  onClick={reopenPreferences}
                  className="px-5 py-2.5 rounded-full bg-white border-2 border-brand-navy/20 text-brand-navy font-semibold text-sm hover:bg-brand-cream transition-colors"
                  data-testid="cookie-open-preferences"
                >
                  Preferințe cookies
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {preferencesOpen && (
        <div
          className="fixed inset-0 z-[350] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closePreferences}
          role="dialog"
          aria-modal="true"
          aria-label="Preferințe cookies"
          data-testid="cookie-preferences-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
          >
            <div className="flex items-center justify-between p-5 sm:p-6 border-b border-brand-beige">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brand-yellow/20 text-brand-navy flex items-center justify-center">
                  <Cookie className="w-5 h-5" />
                </div>
                <div>
                  <h2 className="font-display font-extrabold text-brand-navy text-lg">Preferințe cookies</h2>
                  <p className="text-xs text-brand-navy/60">Alegeți categoriile pe care le acceptați</p>
                </div>
              </div>
              <button type="button" onClick={closePreferences} aria-label="Închide" className="w-9 h-9 rounded-full hover:bg-brand-cream text-brand-navy flex items-center justify-center" data-testid="cookie-preferences-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 sm:p-6 space-y-4">
              {/* NECESARE */}
              <div className="rounded-2xl border-2 border-brand-green/30 bg-brand-green/5 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-brand-green" />
                      <h3 className="font-display font-bold text-brand-navy">Necesare</h3>
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-brand-green text-white font-bold tracking-wider uppercase">Permanent active</span>
                    </div>
                    <p className="text-sm text-brand-navy/75 mt-2 leading-relaxed">
                      Cookie-urile necesare iti permit sa navighezi pe website-ul nostru si sa utilizezi functiile acestuia. Aceste cookie-uri nu colecteaza informatii despre tine care ar putea fi utilizate pentru publicitate sau pentru a inregistra activitatea ta de navigare.
                    </p>
                    <p className="text-xs text-brand-navy/55 mt-2 italic">
                      Pe website-ul nostru: 1 element — <code className="bg-white/60 px-1 rounded">rodental_token</code> (localStorage) pentru autentificarea în panoul administrativ al cabinetului. Nu se plasează cookie-uri terțe pentru statistici sau marketing.
                    </p>
                  </div>
                  <div className="shrink-0 mt-1">
                    <ToggleLocked />
                  </div>
                </div>
              </div>

              {/* TERTE PARTI - Google Maps */}
              <div className="rounded-2xl border-2 border-brand-beige bg-white p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Globe className="w-4 h-4 text-brand-navy" />
                      <h3 className="font-display font-bold text-brand-navy">Conținut terț — Google Maps</h3>
                    </div>
                    <p className="text-sm text-brand-navy/75 mt-2 leading-relaxed">
                      Website-ul incorporeaza hartile Google pentru afisarea locatiei cabinetului. La incarcarea hartii, Google (LLC, S.U.A.) poate seta module cookie pentru functionarea propriului serviciu. Aceste module NU sunt controlate de noi.
                      Fara consimtamantul dumneavoastra, harta nu se incarca — in locul acesteia se afiseaza doar un buton „Afișează harta" si un link direct catre Google Maps.
                    </p>
                    <a
                      href="https://policies.google.com/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block mt-2 text-xs text-brand-red underline hover:text-brand-navy"
                    >
                      Afla mai multe despre acest furnizor →
                    </a>
                  </div>
                  <div className="shrink-0 mt-1">
                    <Toggle checked={draft.thirdParty} onChange={(v) => setDraft({ ...draft, thirdParty: v })} testId="toggle-thirdparty" />
                  </div>
                </div>
              </div>

              <p className="text-xs text-brand-navy/55 italic px-1">
                Nota: pe acest website nu utilizam cookie-uri de statistici, analiza sau marketing. Nu folosim Google Analytics, Meta/Facebook Pixel sau alte instrumente similare.
              </p>
            </div>
            <div className="p-5 sm:p-6 border-t border-brand-beige bg-brand-cream/40 flex flex-wrap gap-3 justify-end">
              <button type="button" onClick={rejectOptional} className="px-5 py-2.5 rounded-full bg-white border-2 border-brand-navy/20 text-brand-navy font-semibold text-sm hover:bg-white" data-testid="cookie-pref-reject">
                Respinge opționale
              </button>
              <button type="button" onClick={acceptAll} className="px-5 py-2.5 rounded-full bg-brand-navy text-white font-semibold text-sm hover:bg-brand-navyDeep" data-testid="cookie-pref-accept-all">
                Acceptă toate
              </button>
              <button type="button" onClick={() => savePreferences(draft)} className="px-5 py-2.5 rounded-full bg-brand-red text-white font-semibold text-sm shadow-md hover:bg-brand-redSoft" data-testid="cookie-pref-save">
                Acceptă selecția
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Toggle({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`relative w-12 h-7 rounded-full transition-colors ${checked ? "bg-brand-red" : "bg-brand-navy/25"}`}
      data-testid={testId}
    >
      <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${checked ? "translate-x-5" : ""}`} />
    </button>
  );
}

function ToggleLocked() {
  return (
    <div className="relative w-12 h-7 rounded-full bg-brand-green/80 cursor-not-allowed" aria-label="Permanent activ, nu poate fi dezactivat">
      <span className="absolute top-0.5 right-0.5 w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center">
        <ShieldCheck className="w-3.5 h-3.5 text-brand-green" />
      </span>
    </div>
  );
}
